import dayjs from 'dayjs';

import {
  PLAYING,
  PAUSED,
  FINISHED,
  CREATING,
  SESSION_STATUSES,
  Session,
  Klass,
} from 'state';

/** Visual "tone" a session status maps to. Only `running` is ever green. */
export type StatusTone =
  | 'running'
  | 'paused'
  | 'finished'
  | 'creating'
  | 'neutral';

export type StatusMeta = {
  label: string;
  tone: StatusTone;
  isRunning: boolean;
  isPaused: boolean;
  isFinished: boolean;
  isCreating: boolean;
};

/**
 * The dashboard's status model. Per the product decision the hero/Active group
 * show EVERY non-finished session (Running, Paused, and the transient
 * Creating/Delay/Frozen states) — only Finished is excluded. Green is reserved
 * strictly for Running.
 */
export const statusMeta = (status: number): StatusMeta => {
  const isRunning = status === PLAYING;
  const isPaused = status === PAUSED;
  const isFinished = status === FINISHED;
  const isCreating = status === CREATING;
  let tone: StatusTone = 'neutral';
  if (isRunning) tone = 'running';
  else if (isPaused) tone = 'paused';
  else if (isFinished) tone = 'finished';
  else if (isCreating) tone = 'creating';
  const label = isRunning
    ? 'LIVE'
    : (SESSION_STATUSES[status as keyof typeof SESSION_STATUSES] ?? 'Unknown');
  return { label, tone, isRunning, isPaused, isFinished, isCreating };
};

/** In progress = anything that is not Finished (finish_date is still null). */
export const isInProgress = (s: Session): boolean => s.status !== FINISHED;
export const isFinished = (s: Session): boolean => s.status === FINISHED;

/** Sort in-progress sessions for the hero strictly by creation date, newest
 *  first (descending create_date) — regardless of running/paused status. */
export const heroOrder = (a: Session, b: Session): number =>
  dayjs(b.create_date).valueOf() - dayjs(a.create_date).valueOf();

/** Newest-first by id (used inside the Active/Finished panel groups). */
export const byNewest = (a: Session, b: Session): number => b.id - a.id;

/** Classes newest-first. `id` is an auto-increment PK, so descending id is
 *  exactly most-recently-created first — using only data already in the store
 *  (no parsing of the loosely-typed `created` field). */
export const classNewestFirst = (a: Klass, b: Klass): number => b.id - a.id;

/** The monospace identifier shown for a class — code, falling back to name. */
export const classCode = (klass?: Pick<Klass, 'code' | 'name'>): string =>
  (klass?.code || klass?.name || '').toString();

/** "today" / "yesterday" / "Nd ago" / "Nmo ago" / "Ny ago". */
export const relativeAgo = (dateStr?: string | null): string => {
  if (!dateStr) return '';
  const d = dayjs(dateStr);
  if (!d.isValid()) return '';
  const days = Math.max(0, dayjs().diff(d, 'day'));
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  // Round (not floor) the coarse month/year buckets: truncating made a session
  // that finished 1y11mo ago read as "1y ago", so two sessions finishing ~1
  // month apart could straddle the 2×365-day line and show "1y" vs "2y".
  if (days < 345) return `${Math.round(days / 30)}mo ago`;
  return `${Math.round(days / 365)}y ago`;
};

/** Elapsed time since a running session started: "Nm/Nh/Nd running". */
export const elapsedRunning = (dateStr?: string | null): string => {
  if (!dateStr) return 'running';
  const d = dayjs(dateStr);
  if (!d.isValid()) return 'running';
  const now = dayjs();
  const mins = Math.max(0, now.diff(d, 'minute'));
  if (mins < 60) return `${mins}m running`;
  const hours = now.diff(d, 'hour');
  if (hours < 24) return `${hours}h running`;
  return `${now.diff(d, 'day')}d running`;
};

/**
 * The timing line for a session card/row.
 * Running -> elapsed; everything else -> "<state> · <relative time>".
 */
export const timingLine = (s: Session): string => {
  const meta = statusMeta(s.status);
  if (meta.isRunning) return elapsedRunning(s.create_date);
  if (meta.isFinished) return `finished · ${relativeAgo(s.finish_date)}`;
  if (meta.isPaused) return `paused · ${relativeAgo(s.create_date)}`;
  return `${meta.label.toLowerCase()} · ${relativeAgo(s.create_date)}`;
};
